import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { api } from '../context/AuthContext';
import { ArrowLeft, Lock, Key } from 'lucide-react';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    resetCode: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.newPassword !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (formData.resetCode.length !== 6) {
      toast.error('Reset code must be 6 digits');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/api/auth/reset-password', {
        email: formData.email,
        reset_code: formData.resetCode,
        new_password: formData.newPassword
      });

      if (response.data.success) {
        toast.success('Password reset successful!', {
          description: 'You can now login with your new password'
        });
        navigate('/login');
      } else {
        toast.error(response.data.message || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(
        error.response?.data?.detail || 
        'Invalid reset code or email. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-zinc-900 border-zinc-800">
        <CardHeader className="space-y-1">
          <div className="flex justify-center mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_barber-cape-coral/artifacts/b9xk2g3p_761.png" 
              alt="Sauce Sity Logo" 
              className="h-28 w-28 object-contain"
            />
          </div>
          <CardTitle className="text-2xl font-bold text-white text-center">Reset Password</CardTitle>
          <CardDescription className="text-zinc-400 text-center">
            Enter the code from your email and create a new password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-white">Email Address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="resetCode" className="text-white flex items-center">
                <Key className="w-4 h-4 mr-2" />
                6-Digit Reset Code
              </Label>
              <Input
                id="resetCode"
                name="resetCode"
                type="text"
                placeholder="000000"
                value={formData.resetCode}
                onChange={handleChange}
                required
                maxLength={6}
                pattern="[0-9]{6}"
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 text-center text-2xl tracking-widest"
              />
              <p className="text-xs text-zinc-500">Enter the code sent to your email</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-white flex items-center">
                <Lock className="w-4 h-4 mr-2" />
                New Password
              </Label>
              <Input
                id="newPassword"
                name="newPassword"
                type="password"
                placeholder="••••••••"
                value={formData.newPassword}
                onChange={handleChange}
                required
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-white">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500"
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="w-full bg-gradient-to-r from-lime-500 to-lime-600 text-black font-semibold hover:from-lime-600 hover:to-lime-700"
            >
              {loading ? 'Resetting Password...' : 'Reset Password'}
            </Button>

            <div className="flex flex-col space-y-2 text-center text-sm">
              <Link 
                to="/forgot-password" 
                className="text-zinc-400 hover:text-lime-400"
              >
                Resend reset code
              </Link>
              <Link 
                to="/login" 
                className="text-zinc-400 hover:text-lime-400 inline-flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to Login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
